import React, { useState, useEffect, useCallback } from 'react';
import axios, { API_URL } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MdAttachMoney, MdReceipt, MdTrendingUp, MdPeople, MdRefresh, MdCreditCard, MdStars } from 'react-icons/md';

const PIE_COLORS = ['#08B23C', '#3B82F6', '#F59E0B', '#EF4444'];

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatMonth(monthStr) {
  const [y, m] = monthStr.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

export default function AdminRevenueAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/revenue-analytics`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load revenue analytics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-gray-500 font-rubik">Failed to load analytics data.</div>;
  }

  const { summary, monthlyTrend, topSpenders, recentTransactions } = data;

  const pieData = [
    { name: 'Stripe Payments', value: summary.stripeTransactions },
    { name: 'Points Redemptions', value: summary.pointsTransactions },
  ].filter(d => d.value > 0);

  const chartData = monthlyTrend.map(m => ({
    month: formatMonth(m.month),
    revenue: m.revenue / 100,
    transactions: m.transactions,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 pt-8 py-6" data-testid="admin-revenue-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <MdAttachMoney className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik">Revenue Analytics</h1>
            <p className="text-sm text-gray-500 font-rubik">Financial overview and transaction analysis</p>
          </div>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-rubik text-sm" data-testid="refresh-revenue-btn">
          <MdRefresh className="text-lg" /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="kpi-total-revenue">
          <div className="flex items-center gap-2 mb-2">
            <MdAttachMoney className="text-primary text-lg" />
            <span className="text-sm font-medium text-gray-500 font-rubik">Total Revenue</span>
          </div>
          <p className="text-3xl font-bold text-gray-800 font-rubik">{formatCents(summary.totalRevenueCents)}</p>
          <p className="text-xs text-gray-400 font-rubik mt-1">{summary.stripeTransactions} paid transaction{summary.stripeTransactions !== 1 ? 's' : ''}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="kpi-total-transactions">
          <div className="flex items-center gap-2 mb-2">
            <MdReceipt className="text-blue-500 text-lg" />
            <span className="text-sm font-medium text-gray-500 font-rubik">Transactions</span>
          </div>
          <p className="text-3xl font-bold text-gray-800 font-rubik">{summary.totalTransactions}</p>
          <p className="text-xs text-gray-400 font-rubik mt-1">{summary.stripeTransactions} paid + {summary.pointsTransactions} points</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="kpi-avg-transaction">
          <div className="flex items-center gap-2 mb-2">
            <MdTrendingUp className="text-amber-500 text-lg" />
            <span className="text-sm font-medium text-gray-500 font-rubik">Avg Transaction</span>
          </div>
          <p className="text-3xl font-bold text-gray-800 font-rubik">{formatCents(summary.avgTransactionCents)}</p>
          <p className="text-xs text-gray-400 font-rubik mt-1">Per paid transaction</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="kpi-subscriptions">
          <div className="flex items-center gap-2 mb-2">
            <MdPeople className="text-purple-500 text-lg" />
            <span className="text-sm font-medium text-gray-500 font-rubik">Subscriptions</span>
          </div>
          <p className="text-3xl font-bold text-gray-800 font-rubik">{summary.activeSubscriptions}</p>
          <p className="text-xs text-gray-400 font-rubik mt-1">{summary.payingSubscriptions} paying</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border p-6" data-testid="monthly-revenue-chart">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-4">Monthly Revenue</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fontFamily: 'Rubik' }} />
                <YAxis tick={{ fontSize: 12, fontFamily: 'Rubik' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ fontFamily: 'Rubik', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#08B23C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-gray-400 font-rubik text-sm">
              No revenue data yet
            </div>
          )}
        </div>

        {/* Transaction Breakdown Pie */}
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="transaction-breakdown-chart">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-4">Transaction Types</h2>
          {pieData.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4}>
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontFamily: 'Rubik', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-2">
                {pieData.map((item, idx) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                      <span className="text-gray-600 font-rubik">{item.name}</span>
                    </div>
                    <span className="font-semibold text-gray-800 font-rubik">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-gray-400 font-rubik text-sm">
              No transactions yet
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Top Spenders + Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Top Spenders */}
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="top-spenders">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-4 flex items-center gap-2">
            <MdStars className="text-amber-500" /> Top Spenders
          </h2>
          {topSpenders.length > 0 ? (
            <div className="space-y-3">
              {topSpenders.map((user, idx) => (
                <div key={user.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`top-spender-${idx}`}>
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center font-rubik flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 font-rubik truncate">@{user.username}</p>
                    <p className="text-xs text-gray-400 font-rubik">{user.transactions} txns &middot; {user.points_spent} pts</p>
                  </div>
                  <span className="text-sm font-bold text-primary font-rubik flex-shrink-0">
                    {formatCents(user.revenue_cents)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-6 text-gray-400 text-sm font-rubik">No spender data yet</p>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border" data-testid="recent-transactions">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
              <MdCreditCard className="text-blue-500" /> Recent Transactions
            </h2>
          </div>
          {recentTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">User</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Question</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 font-rubik">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50" data-testid={`tx-row-${tx.id}`}>
                      <td className="px-4 py-3 font-rubik text-gray-800">@{tx.username}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium font-rubik ${
                          tx.type === 'stripe' ? 'bg-green-100 text-green-700' :
                          tx.type === 'subscription' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {tx.type === 'stripe' ? 'Payment' : tx.type === 'subscription' ? 'Subscription' : 'Points'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-rubik font-medium text-gray-800">
                        {tx.amount_cents > 0 ? formatCents(tx.amount_cents) : `${tx.points_used} pts`}
                      </td>
                      <td className="px-4 py-3 font-rubik text-gray-600 max-w-[200px] truncate" title={tx.poll_question}>
                        {tx.poll_question || '—'}
                      </td>
                      <td className="px-4 py-3 font-rubik text-gray-500 whitespace-nowrap">
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400 font-rubik text-sm">No transactions yet</div>
          )}
        </div>
      </div>

      {/* Points Summary */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-8" data-testid="points-summary">
        <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-4">Points Economy</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs font-medium text-blue-600 font-rubik mb-1">Total Points Redeemed</p>
            <p className="text-2xl font-bold text-blue-800 font-rubik">{summary.totalPointsSpent.toLocaleString()}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-xs font-medium text-green-600 font-rubik mb-1">Points Transactions</p>
            <p className="text-2xl font-bold text-green-800 font-rubik">{summary.pointsTransactions}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-xs font-medium text-purple-600 font-rubik mb-1">Avg Points / Redemption</p>
            <p className="text-2xl font-bold text-purple-800 font-rubik">
              {summary.pointsTransactions > 0 ? Math.round(summary.totalPointsSpent / summary.pointsTransactions) : 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
